/**
 * The answer key of a star battle is the set of stars. Kept apart from the form component so
 * the eagerly loaded module file never pulls the author's board into the player bundle.
 */
import { blankRows, resizeRows } from "./progress";
import { EMPTY, STAR, type StarBattleDefinition, type StarBattleSolution } from "./types";

/**
 * What the core hands the form when nothing is queued: no rows at all, so the form can tell
 * "nothing typed" from a deliberately empty grid. `starsOf` pads it to a blank grid.
 */
export function emptySolution(_definition: StarBattleDefinition): StarBattleSolution {
  return { rows: [] };
}

/** Whether a stored value is a star grid at all, whatever its size. */
export function isStarGrid(value: unknown): value is StarBattleSolution {
  if (typeof value !== "object" || value === null || Array.isArray(value)) return false;
  const rows = (value as { rows?: unknown }).rows;
  return Array.isArray(rows) && rows.every((row) => typeof row === "string" && /^[*.]*$/.test(row));
}

/** Nothing queued: not a star grid, or the empty one the core hands out. */
export function isAbsent(value: unknown): boolean {
  return !isStarGrid(value) || value.rows.length === 0;
}

/** Reads whatever is stored, however wrong, as a star grid of the definition's size. */
export function starsOf(value: unknown, definition: StarBattleDefinition): string[] {
  if (!isStarGrid(value)) return blankRows(definition.size);
  return resizeRows(
    value.rows.map((row) => Array.from(row, (c) => (c === STAR ? STAR : EMPTY)).join("")),
    definition.size,
  );
}
